import { Injectable, Optional } from '@angular/core';
import { Account, ActiveStatusEnum, ProgramPcardTypeEnum, ProgramPurchaseOrderTypeEnum } from '@models';
import * as _ from 'lodash-es';
import { AccountDbQueryService } from './account-db-query.service';
import { Router } from '@angular/router';
import { Roles } from '@core';
import { StringFns } from '@utils';
import { ActivationBaseService, ICheckListItem } from '@services';

// TODO: Consider making each checklistItem test be async to allow for more complex checks that may require db calls

@Injectable({
  providedIn: 'root',
})
export class AccountActivationService extends ActivationBaseService {
  private activatedAccount!: Account;

  constructor(
    private dbQuery: AccountDbQueryService,
    private router: Router,
  ) {
    super();
    this.loadChecklist();
  }

  loadChecklist() {
    this.addToChecklist({
      description: 'User Import Code requirement is required',
      route: 'info',
      test: (checkItem: ICheckListItem) => {
        return this.activatedAccount.accountImportCodeOptionId != null;
      },
    });

    this.addToChecklist({
      description: 'Principal Account Administrator is required, active and of type PrincipalAccountAdministrator',
      route: 'info',
      test: (checkItem: ICheckListItem) => {
        if (this.activatedAccount.primaryAccountAdminId == null) {
          return false;
        }
        if (
          this.activatedAccount.primaryAccountAdmin.getStatusId() != ActiveStatusEnum.Active ||
          this.activatedAccount.primaryAccountAdmin.getRoleName() != Roles.PrincipalAccountAdmin
        ) {
          return false;
        }
        return true;
      },
    });

    this.addToChecklist({
      description: 'All Account Administrators must have e-mail addresses',
      route: 'admin',
      test: (checkItem: ICheckListItem) => {
        const admins = this.activatedAccount.accountAdmins.filter((r) => {
          const roleName = r.proximityUser.proximityRole?.name;
          return (
            r.proximityUser.activeStatusId === ActiveStatusEnum.Active &&
            (roleName === Roles.PrincipalAccountAdmin || roleName === Roles.AccountAdmin)
          );
        });
        const badAdmins = admins.filter((r) => StringFns.isNullEmptyOrWhiteSpace(<string>r.proximityUser.email));
        checkItem.errors = badAdmins.map((r) => r.proximityUser.fullName);
        return checkItem.errors.length == 0;
      },
    });

    this.addToChecklist({
      description: 'At least one Administrator Group is required',
      route: 'ag',
      test: (item: ICheckListItem) => {
        return this.activatedAccount.accountAdminGroups.length >= 0;
      },
    });

    this.addToChecklist({
      description: 'Each Administrator Group must have at least one Active Administrator',
      route: 'ag',
      test: (checkItem: ICheckListItem) => {
        const badAgs = this.activatedAccount.accountAdminGroups.filter((x) => x.accountAdminGroupMaps.length == 0);
        checkItem.errors = badAgs.map((x) => x.name);
        return checkItem.errors.length == 0;
      },
    });

    this.addToChecklist({
      description: 'At least one Active Program User Group is required',
      route: 'pug',
      test: (checkItem: ICheckListItem) => {
        return this.activatedAccount.programUserGroups.filter((x) => x.activeStatusId == ActiveStatusEnum.Active).length > 0;
      },
    });

    this.addToChecklist({
      description: 'Each Active Program User Group requires at least one Account User',
      route: 'pug',
      test: (checkItem: ICheckListItem) => {
        const badPugs = this.activatedAccount.programUserGroups.filter(
          (p) => p.activeStatusId == ActiveStatusEnum.Active && p.programUserGroupMaps.length == 0,
        );
        checkItem.errors = badPugs.map((p) => p.name);
        return checkItem.errors.length == 0;
      },
    });

    this.addToChecklist({
      description: 'Each Active Shipping User Group requires at least one Account User',
      route: 'ship',
      test: (checkItem: ICheckListItem) => {
        const badSugs = this.activatedAccount.shippingUserGroups.filter(
          (p) => p.activeStatusId == ActiveStatusEnum.Active && p.shippingUserGroupMaps.length == 0,
        );
        checkItem.errors = badSugs.map((p) => p.name);
        return checkItem.errors.length == 0;
      },
    });

    // this.addToChecklist({
    //   description: 'Active Shipping User Group requires a purchase order',
    //   route: 'ship',
    // });

    // this.addToChecklist({
    //   description: 'Active Shipping User Group purchase order is expired and/or not active',
    //   route: 'ship',
    // });

    // this.addToChecklist({
    //   description: 'Active Shipping User Group requires a procurement card (p-card)',
    //   route: 'ship',
    // });

    // this.addToChecklist({
    //   description: 'Active Shipping User Group procurement card (p-card) is expired and/or not active',
    //   route: 'ship',
    // });

    this.addToChecklist({
      description: 'Active Program User Group requires a purchase order',
      route: 'org',
      test: (checkItem: ICheckListItem) => {
        const pros = this.activatedAccount.programs.filter((pro) => {
          return (
            pro.activeStatusId === ActiveStatusEnum.Active &&
            pro.programPurchaseOrderTypeId === ProgramPurchaseOrderTypeEnum.ByProgramUserGroup &&
            pro.isPurchaseOrderRequired
          );
        });
        const atugs = _.flatMap(pros.map((pro) => pro.approvalTree.approvalTreeUserGroups));
        const badAtugs = atugs.filter((atug) => atug.accountPurchaseOrderId == null);
        checkItem.errors = badAtugs.map((atug) => atug.programUserGroup.name);
        return checkItem.errors.length == 0;
      },
    });

    this.addToChecklist({
      description: 'Active Program User Group purchase order is expired and/or not active',
      route: 'org',
      test: (checkItem: ICheckListItem) => {
        const pros = this.activatedAccount.programs.filter((pro) => {
          return (
            pro.activeStatusId === ActiveStatusEnum.Active &&
            pro.programPurchaseOrderTypeId === ProgramPurchaseOrderTypeEnum.ByProgramUserGroup &&
            pro.isPurchaseOrderRequired
          );
        });
        const atugs = _.flatMap(pros.map((pro) => pro.approvalTree.approvalTreeUserGroups));
        const badAtugs = atugs.filter(
          (atug) =>
            atug.accountPurchaseOrder != null &&
            atug.activeStatusId === ActiveStatusEnum.Active &&
            !atug.accountPurchaseOrder.isCurrentAndActive(),
        );
        checkItem.errors = badAtugs.map((atug) => atug.programUserGroup.name);
        return checkItem.errors.length == 0;
      },
    });

    this.addToChecklist({
      description: 'Active Program User Group requires a procurement card (p-card)',
      route: 'org',
      test: (checkItem: ICheckListItem) => {
        const pros = this.activatedAccount.programs.filter((pro) => {
          return (
            pro.activeStatusId === ActiveStatusEnum.Active &&
            pro.programPurchaseOrderTypeId === ProgramPurchaseOrderTypeEnum.ByProgramUserGroup &&
            pro.isPcardRequired
          );
        });
        const atugs = _.flatMap(pros.map((pro) => pro.approvalTree.approvalTreeUserGroups));
        const badAtugs = atugs.filter((atug) => atug.accountProcurementCardId == null);
        checkItem.errors = badAtugs.map((atug) => atug.programUserGroup.name);
        return checkItem.errors.length == 0;
      },
    });

    this.addToChecklist({
      description: 'Active Program User Group procurement card (p-card) is expired and/or not active',
      route: 'org',
      test: (checkItem: ICheckListItem) => {
        const pros = this.activatedAccount.programs.filter((pro) => pro.activeStatusId === ActiveStatusEnum.Active);
        const atugs = _.flatMap(pros.map((pro) => pro.approvalTree.approvalTreeUserGroups));
        const badAtugs = atugs.filter(
          (atug) =>
            atug.accountProcurementCard != null &&
            atug.activeStatusId === ActiveStatusEnum.Active &&
            !atug.accountProcurementCard.isCurrentAndActive(),
        );
        checkItem.errors = badAtugs.map((atug) => atug.accountProcurementCard.pCardName);
        return checkItem.errors.length == 0;
      },
    });

    this.addToChecklist({
      description: 'At least one Proximity Organization Tree is required',
      route: 'org',
      test: (item: ICheckListItem) => {
        return this.activatedAccount.approvalTrees.length > 0;
      },
    });

    this.addToChecklist({
      description: 'Each Proximity Organization Tree requires a top-level Administrator Group',
      route: 'org',
      test: (checkItem: ICheckListItem) => {
        const badAts = this.activatedAccount.approvalTrees.filter((at) => at.approvalTreeAdminGroups.length == 0);
        checkItem.errors = badAts.map((at) => at.name);
        return checkItem.errors.length == 0;
      },
    });

    // this.addToChecklist({
    //   description: 'Every Account Administrator Group must have at least 1 active administrator',
    //   route: 'ag',
    //   test: (checkItem: ICheckListItem) => {
    //     const badAgs = this.activatedAccount.accountAdminGroups.filter(
    //       (ag) =>
    //         ag.accountAdminGroupMaps.filter((agm) => agm.accountAdmin.proximityUser.activeStatusId == ActiveStatusEnum.Active).length == 0,
    //     );
    //     checkItem.errors = badAgs.map((at) => at.name);
    //     return checkItem.errors.length == 0;
    //   },
    // });
  }

  routeTo(route: string) {
    if (route === 'info') {
      this.router.navigate(['/account/accounts', this.activatedAccount?.id, 'manage', 'account-info']);
    }

    if (route === 'ag') {
      this.router.navigate(['/account/accounts', this.activatedAccount?.id, 'manage', 'account-admin-groups']);
    }

    if (route === 'pug') {
      this.router.navigate(['/account/accounts', this.activatedAccount?.id, 'manage', 'program-user-groups']);
    }

    if (route === 'ship') {
      this.router.navigate(['/account/accounts', this.activatedAccount?.id, 'manage', 'shipping-user-groups']);
    }

    if (route === 'org') {
      this.router.navigate(['/account/accounts', this.activatedAccount?.id, 'manage', 'approval-trees']);
    }

    if (route == 'admin') {
      this.router.navigate(['/account/accounts', this.activatedAccount?.id, 'manage', 'account-administrators']);
    }
  }

  public async activate(accountId: string): Promise<boolean> {
    this.activatedAccount = await this.dbQuery.getAccountForValidationById(accountId);
    if (this.activatedAccount == null) return true;

    return this.executeTests();
  }
}
